'use client';

import React, { useState } from 'react';
import { Box, Typography, Button, Dialog } from 'src/components';
import { deleteCRMIntegration } from 'src/api/integrations/integrationsApi';
import { useNotification } from 'src/context/notification';
import { useProfile } from 'src/context/providers/ProfileProvider';

interface DeleteCRMLinkDialogProps {
  isOpen: boolean;
  onClose: () => void;
  crmLink: {
    id: number;
    crm: string;
    crm_object_type: string;
  } | null;
  onDeleteSuccess?: () => void;
}

const DeleteCRMLinkDialog = ({
  isOpen,
  onClose,
  crmLink,
  onDeleteSuccess,
}: DeleteCRMLinkDialogProps): React.ReactElement => {
  const [isDeleting, setIsDeleting] = useState(false);
  const { setNotification } = useNotification();
  const { refetch } = useProfile();

  const crmDisplayName =
    crmLink?.crm?.charAt?.(0)?.toUpperCase() +
    crmLink?.crm?.slice?.(1)?.toLowerCase();

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!crmLink) return;

    setIsDeleting(true);

    try {
      await deleteCRMIntegration({ id: crmLink.id });

      // Refetch entity details to update CRM links
      await refetch();

      setNotification({
        title: 'Success',
        message: `CRM link to ${crmDisplayName} has been deleted.`,
        type: 'success',
      });

      if (onDeleteSuccess) {
        onDeleteSuccess();
      }

      onClose();
    } catch (error) {
      console.error('Error deleting CRM link:', error);
      setNotification({
        title: 'Error',
        message: error?.error_message || 'Failed to delete CRM link.',
        type: 'error',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      aria-labelledby="delete-crm-link-dialog"
      size="sm"
      hideBackdrop={false}
      className="!h-auto"
    >
      <Dialog.Content>
        <Box className="p-6" onClick={(e) => e.stopPropagation()}>
          <Typography
            data-testid="delete-crm-link-dialog-title"
            className="text-neutral-800 mb-4"
          >
            Are you sure you want to delete this CRM link to {crmDisplayName}?
            This action cannot be undone.
          </Typography>
          <Box className="flex justify-end gap-3 mt-6">
            <Button
              variant="outlined"
              color="neutral"
              onClick={onClose}
              className="px-4"
              data-testid="delete-crm-link-dialog-cancel-button"
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="solid"
              color="danger"
              onClick={handleDelete}
              className="px-4"
              data-testid="delete-crm-link-dialog-confirm-button"
              loading={isDeleting}
              disabled={isDeleting}
            >
              Confirm
            </Button>
          </Box>
        </Box>
      </Dialog.Content>
    </Dialog>
  );
};

export default DeleteCRMLinkDialog;
